import { useFormContext } from 'react-hook-form';
import { Chip } from 'shared/mui/Chips';
import {
  DEFAULT_DATE_TIME_MASK,
  DEFAULT_SEPARATOR,
} from 'shared/mui/DatePicker/config/defaultValues';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { PopoverChip } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips/components/PopoverChip/PopoverChip';
import { formatInputInTz } from 'utils/date';

export type FilterDateTimeRangeTimezoneChipProps = {
  label: string;
  name: string;
  children: React.ReactNode;
  onApply?: () => void;
  onDelete?: () => void;
  mask?: string;
  timezoneName?: string;
};

export const FilterDateTimeRangeTimezoneChip: React.FC<FilterDateTimeRangeTimezoneChipProps> = ({
  children,
  label,
  name,
  onApply,
  onDelete,
  mask = DEFAULT_DATE_TIME_MASK,
  timezoneName = 'timezone',
}) => {
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();

  const { appliedFilters, setAppliedFilter } = useFilterContext();
  const { getValues, setValue } = useFormContext();

  if (!appliedFilters[name] || (!appliedFilters[name]?.[0] && !appliedFilters[name]?.[1])) {
    return <></>;
  }

  function onApplyHandler() {
    handleClose();
    onApply && onApply();
    setAppliedFilter(name, getValues(name));
    setAppliedFilter(timezoneName, getValues(timezoneName));
  }

  function onReset() {
    setAppliedFilter(name, appliedFilters?.[name]);
    setValue(name, appliedFilters?.[name]);
    setValue(timezoneName, appliedFilters?.[timezoneName]);
  }

  function onDeleteHandler() {
    onDelete && onDelete();
    setValue(name, [null, null]);
    setAppliedFilter(name, [null, null]);
  }

  const open = Boolean(anchorEl);
  const [start, end] = appliedFilters?.[name] ?? [];
  const timezone = appliedFilters?.[timezoneName];
  const labelText = `${formatInputInTz(start, { format: mask, timezone })}${DEFAULT_SEPARATOR}${formatInputInTz(end, { format: mask, timezone })}`;
  return (
    <>
      <PopoverChip
        anchorEl={anchorEl}
        onApply={onApplyHandler}
        onClose={handleClose}
        onReset={onReset}
        open={open}
      >
        {children}
      </PopoverChip>
      <Chip
        label={`${label}: ${labelText}`}
        onClick={handleClick}
        onDelete={onDeleteHandler}
        size="medium"
      />
    </>
  );
};
